import { Responsive } from '@cian/ui-kit';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getArchivedListNextPage } from '../../actions/archivedList';
import { getChatListNextPage, setChatListLoadPageSucceed } from '../../actions/chatList';
import { getSpamListNextPage } from '../../actions/spamList';
import { getUnreadListNextPage } from '../../actions/unreadList';
import { ChatList, ChatListLayout } from '../../components/ChatList';
import { ChatListSkeleton } from '../../components/ChatListSkeleton';
import { ChatListSupportItem } from '../../components/ChatListSupportItem';
import { ChatListWrapperLayout } from '../../components/ChatListWrapperLayout';
import { EmptyChatList } from '../../components/EmptyChatList';
import { useChatsToDisplay } from '../../hooks/useChatsToDisplay';
import { useFeedbackForm } from '../../hooks/useFeedbackForm';
import { chatListSelector, currentOpenChatIDSelector, hostTypeSelector, userSelector } from '../../selectors';
import { ERequestApiStatus } from '../../types/appState';
import { EHostType } from '../../types/hostType';
import { ERoute } from '../../types/route';
import { isIframeHostType } from '../../utils/isIframeHostType';
import { LeftSwipeVisibleProvider } from '../../utils/useLeftSwipeVisible';
import { AuthNotificationContainer } from '../AuthNotificationContainer';
import { ChatListChatbotItemContainer } from '../ChatListChatbotItemContainer';
import { ChatListContextMenuContainer } from '../ChatListContextMenuContainer';
import { ChatListItemContainer } from '../ChatListItemContainer';
import { ListActionButtonsContainer } from '../ListActionButtonsContainer';
import { TopMenuContainer } from '../TopMenuContainer';

export const ChatListContainer: React.FC = () => {
  const dispatch = useDispatch();
  const { requestFeedbackForm } = useFeedbackForm();

  const { currentRoute, allChatsLoaded, status, isFirstLoad } = useSelector(chatListSelector);
  const hostType = useSelector(hostTypeSelector);

  const { isAuthenticated } = useSelector(userSelector);
  const currentOpenChatId = useSelector(currentOpenChatIDSelector);
  const isFrame = isIframeHostType(hostType);

  const listRef = React.useRef<HTMLDivElement>(null);
  const openedChatRef = React.useRef<HTMLDivElement>(null);

  const chatsToDisplay = useChatsToDisplay();

  const [isFirstScroll, setIsFirstScroll] = React.useState(!!currentOpenChatId);

  const getNextPage = React.useCallback(() => {
    if (currentRoute === ERoute.Spam) {
      return dispatch(getSpamListNextPage());
    }

    if (currentRoute === ERoute.Messages) {
      return dispatch(getChatListNextPage());
    }

    if (currentRoute === ERoute.Archive) {
      return dispatch(getArchivedListNextPage());
    }

    if (currentRoute === ERoute.Unread) {
      return dispatch(getUnreadListNextPage());
    }

    return;
  }, [dispatch, currentRoute]);

  const onScrollToEnd = React.useCallback(() => {
    if (allChatsLoaded || status === ERequestApiStatus.Pending) {
      return;
    }

    getNextPage();
  }, [allChatsLoaded, status, getNextPage]);

  React.useEffect(() => {
    if (status === ERequestApiStatus.Succeed) {
      dispatch(setChatListLoadPageSucceed());
    }
  }, [status, currentRoute, dispatch]);

  React.useEffect(() => {
    if (hostType !== EHostType.Frame && isAuthenticated && isFirstLoad) {
      getNextPage();
    }
  }, [status, isAuthenticated, getNextPage, hostType, isFirstLoad]);

  React.useEffect(() => {
    // при открытии чата по chatid нужно прокрутить его в видимую область
    if (
      hostType !== EHostType.Frame &&
      chatsToDisplay.length &&
      isFirstScroll &&
      !isFirstLoad &&
      openedChatRef.current
    ) {
      openedChatRef.current.scrollIntoView({ behavior: 'auto', block: 'center', inline: 'nearest' });
      setIsFirstScroll(false);
    }
  }, [hostType, isFirstLoad, chatsToDisplay.length, isFirstScroll]);

  const chatListContent = React.useMemo(() => {
    if (isAuthenticated && status === ERequestApiStatus.Pending && !chatsToDisplay.length) {
      return <ChatListSkeleton />;
    }

    return chatsToDisplay.length ? (
      <ChatList status={status} onScrollToEnd={onScrollToEnd} listRef={listRef}>
        <LeftSwipeVisibleProvider>
          {currentRoute === ERoute.Messages && (
            <>
              {!isFrame && <ChatListSupportItem onClick={requestFeedbackForm} />}
              <ChatListChatbotItemContainer />
            </>
          )}

          {chatsToDisplay.map(chat => (
            <ChatListItemContainer chat={chat} key={`chat-item-${chat.chatId}`} openedChatRef={openedChatRef} />
          ))}
        </LeftSwipeVisibleProvider>
      </ChatList>
    ) : (
      <>
        {currentRoute === ERoute.Messages && (
          <ChatListWrapperLayout>
            {!isFrame && <ChatListSupportItem onClick={requestFeedbackForm} />}
            <ChatListChatbotItemContainer />
          </ChatListWrapperLayout>
        )}

        {!isAuthenticated && (
          <Responsive phone tablet>
            <AuthNotificationContainer />
          </Responsive>
        )}
        {isAuthenticated && status === ERequestApiStatus.Succeed ? <EmptyChatList route={currentRoute} /> : null}
      </>
    );
  }, [chatsToDisplay, currentRoute, isAuthenticated, onScrollToEnd, requestFeedbackForm, status, isFrame]);

  const props = React.useMemo(
    () => ({
      topMenu: (
        <TopMenuContainer
          isChangeListVisible={Boolean(chatsToDisplay.length && currentRoute !== ERoute.Spam && isAuthenticated)}
        />
      ),
      chatListContent,
      listActionButtons: <ListActionButtonsContainer />,
      contextMenu: <ChatListContextMenuContainer listRef={listRef} />,
    }),
    [chatListContent, chatsToDisplay.length, currentRoute, isAuthenticated],
  );

  return <ChatListLayout {...props} />;
};
